import _ from 'lodash';
import { call, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import {
    putAndHandleAPIError,
    putAndWaitHandleAllAPIError,
    selectAPI
} from '@api/API.sagas';
import * as lendingApiActions from '@api/lendingAPI/LendingAPI.actions';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { CLOSE_MODAL, OPEN_MODAL } from '../../iwModals/Modals.actions';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { FLEXI_12_URL } from '../dashboard/Dashboard.sagas';
import { DOCUMENT_UPLOADER } from '../modals/uploadDocumentsModal/documentUploader/DocumentUploader.actions';
import {
    FLEXI_24_PRODUCT_TYPE,
    FLEXI_LOAN_PRODUCT_TYPE,
    getOutstandingPrincipal
} from '../multiLoanDashboard/accountsSelector';

import { hasCreatedFlexiLoanApplicationInAccountPages as hasCreatedFlexiLoanApplicationInAccountPagesSelector } from './applyFlexiLoanSelectors';
import * as applyViewActions from './ApplyFlexiLoanView.actions';
import { tracking } from '@iwoca/frontend-tracking-library';
import { formaliseOffers } from '@api/formaliseOffers';

export const applyFlexiLoanSagasWorker = [
    fork(activateApplyFlexiLoan),
    fork(createFlexiLoanApplicationWorker),
    fork(submitFlexiLoanApplicationWorker),
    fork(uploadFlexiLoanDocumentRequirement),
    fork(getLatestDocumentRequirementsWorker)
];

function* activateApplyFlexiLoan() {
    yield takeLatest(applyViewActions.ACTIVATE_APPLY_FLEXI_LOAN, function* () {
        const [, applicationResponse] = yield putAndWaitHandleAllAPIError([
            { type: lendingApiActions.API_GET_CUSTOMER_STATE },
            { type: lendingApiActions.API_GET_APPLICATION },
            { type: lendingApiActions.API_GET_ACCOUNTS }
        ]);

        // If a customers app is deferred, they should be able to create a new app.
        // As their hasCreatedFlexiLoan flag has never been unset, I'm unsetting it here, so they can go through the flow and create an app on step 1
        const isDeferred =
            _.get(applicationResponse, 'response.data.status') === 'deferred';

        if (isDeferred) {
            const { customer_state } = yield selectAPI();
            const stateData = _.cloneDeep(customer_state);
            stateData.ui.hasCreatedFlexiApplication = null;

            yield put({
                type: lendingApiActions.API_GET_CUSTOMER_STATE_EDIT,
                data: stateData
            });

            yield putAndHandleAPIError({
                type: lendingApiActions.API_SAVE_CUSTOMER_STATE
            });
        }

        const hasCreatedFlexiLoanApplicationInAccountPages = yield select(
            hasCreatedFlexiLoanApplicationInAccountPagesSelector
        );

        if (hasCreatedFlexiLoanApplicationInAccountPages) {
            yield put({
                type: applyViewActions.SHOW_UPLOAD_DOCUMENTS_STEP
            });

            const isInNewUploadDocsSplitTest = yield select(
                (state) => state?.siteContext?.splitTestsNewDocUploads
            );

            if (!isInNewUploadDocsSplitTest) {
                yield getLatestDocumentRequirements();
            }
        }

        yield put({ type: applyViewActions.APPLY_FLEXI_LOAN_DATA });
    });
}

function* createFlexiLoanApplicationWorker() {
    yield takeLatest(applyViewActions.CREATE_APPLICATION, function* () {
        const isTopUpApplication = getQueryParameter(
            getCurrentLocation(),
            'topup'
        );
        const isIwocaPayCrossSell =
            getQueryParameter(getCurrentLocation(), 'ipxsell') === '1';

        if (isTopUpApplication) {
            tracking.ampli.topupReapplyFormApplicationStepSubmitted();
        } else if (!isTopUpApplication) {
            tracking.ampli.newLoanApplicationStepSubmitted();
        }

        yield put({
            type: applyViewActions.CREATE_APPLICATION_PENDING
        });

        const {
            ApplyForm: { values: ApplyFlexiLoanFormValues }
        } = yield select(({ form }) => form);

        const isFlexi24Topup =
            getCountry() === 'UK' &&
            getQueryParameter(getCurrentLocation(), 'product_type') ===
                FLEXI_24_PRODUCT_TYPE;

        const isDeRepeatFlow = getCountry() === 'DE';

        const durationDataDE = {
            duration: {
                unit: 'months',
                amount: parseInt(ApplyFlexiLoanFormValues.duration)
            }
        };

        const applicationData = {
            data: {
                requested_products: [
                    {
                        product_type: isFlexi24Topup
                            ? FLEXI_24_PRODUCT_TYPE
                            : FLEXI_LOAN_PRODUCT_TYPE,
                        amount: ApplyFlexiLoanFormValues.amount,
                        purpose: ApplyFlexiLoanFormValues.purpose,
                        ...(isDeRepeatFlow && durationDataDE)
                    }
                ]
            }
        };

        if (isTopUpApplication) {
            yield putAndHandleAPIError({
                type: lendingApiActions.API_GET_ACCOUNTS
            });

            const outstandingPrincipal = yield select(getOutstandingPrincipal);
            const topupAmount =
                ApplyFlexiLoanFormValues.amount + outstandingPrincipal;

            applicationData.data.requested_products[0].amount = topupAmount;
            applicationData.data.requested_products[0].requested_cash_amount =
                ApplyFlexiLoanFormValues.amount;
        }

        const { customer_state } = yield selectAPI();

        const stateData = _.merge({}, customer_state, {
            application: {
                company: {
                    last_12_months_turnover: {
                        amount: ApplyFlexiLoanFormValues.turnover,
                        datetime: new Date().toISOString()
                    }
                }
            }
        });

        yield put({
            type: lendingApiActions.API_GET_CUSTOMER_STATE_EDIT,
            data: stateData
        });

        yield putAndHandleAPIError({
            type: lendingApiActions.API_SAVE_CUSTOMER_STATE
        });

        yield putAndHandleAPIError({
            type: lendingApiActions.API_PUT_APPLICATION,
            body: JSON.stringify(applicationData)
        });

        const application = yield putAndHandleAPIError({
            type: lendingApiActions.API_GET_APPLICATION
        });

        if (isIwocaPayCrossSell) {
            tracking.ampli.buyerSubmittedXSellApplication({
                application_id: application.response.data.application_id
            });
        }

        yield getApplicationDecision();

        yield put({
            type: applyViewActions.CREATE_APPLICATION_SUCCESS
        });
    });
}

function* getApplicationDecision() {
    const stateKey = yield select(
        ({ siteContext }) =>
            siteContext.impersonatingStateKey || siteContext.state_key
    );
    const { result } = yield call(formaliseOffers, {
        stateKey,
        useAutoFormalisation: true
    });

    if (result === 'FORMALISED' || result === 'DECLINED') {
        const { application } = yield selectAPI();
        const isTopUpApplication = getQueryParameter(
            getCurrentLocation(),
            'topup'
        );

        if (isTopUpApplication) {
            tracking.ampli.topupRequestCompleted({
                application_id: application.application_id
            });
        } else {
            tracking.ampli.newLoanRequestCompleted({
                application_id: application.application_id
            });
        }

        yield put({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: FLEXI_12_URL }
        });
    } else {
        yield putAndHandleAPIError({
            type: lendingApiActions.API_GET_PRODUCT_OFFERS
        });

        yield put({
            type: applyViewActions.SHOW_UPLOAD_DOCUMENTS_STEP
        });

        const { customer_state } = yield selectAPI();

        const stateData = _.merge({}, customer_state, {
            ui: {
                hasCreatedFlexiApplication: true
            }
        });

        yield put({
            type: lendingApiActions.API_GET_CUSTOMER_STATE_EDIT,
            data: stateData
        });

        yield putAndHandleAPIError({
            type: lendingApiActions.API_SAVE_CUSTOMER_STATE
        });
    }
}

function* getLatestDocumentRequirementsWorker() {
    yield takeLatest(
        applyViewActions.APPLY_FLEXI_LOAN_LOAD_LATEST_DOCUMENT_REQUIREMENTS,
        getLatestDocumentRequirements
    );
}

function* getLatestDocumentRequirements() {
    yield put({ type: applyViewActions.DOCUMENT_REQUIREMENTS_LOADING });

    yield putAndHandleAPIError({
        type: lendingApiActions.API_GET_PRODUCT_OFFERS
    });

    yield put({ type: applyViewActions.DOCUMENT_REQUIREMENTS_COMPLETE });
}

function* uploadFlexiLoanDocumentRequirement() {
    yield takeLatest(
        applyViewActions.UPLOAD_FLEXI_LOAN_DOCUMENT_REQUIREMENT,
        function* (action) {
            yield put({
                type: DOCUMENT_UPLOADER,
                preventBackNavigation: true,
                documentType: action.documentType
            });

            yield put({
                type: OPEN_MODAL,
                id: 'UploadDocumentsModal'
            });

            yield take(CLOSE_MODAL);

            yield* getLatestDocumentRequirements();
        }
    );
}

function* submitFlexiLoanApplicationWorker() {
    yield takeLatest(
        applyViewActions.APPLY_FLEXI_LOAN_SUBMIT_APPLICATION,
        function* () {
            const { application } = yield selectAPI();
            const isTopUpApplication = getQueryParameter(
                getCurrentLocation(),
                'topup'
            );

            if (isTopUpApplication) {
                tracking.ampli.topupReapplyFormDocumentsStepSubmitted({
                    application_id: application.application_id
                });
            } else {
                tracking.ampli.newLoanDocumentsStepSubmitted({
                    application_id: application.application_id
                });
            }

            yield put({
                type: applyViewActions.APPLY_FLEXI_LOAN_SUBMIT_APPLICATION_LOADING
            });

            const { customer_state } = yield selectAPI();
            const stateData = _.cloneDeep(customer_state);
            stateData.ui.hasCreatedFlexiApplication = null;

            yield put({
                type: lendingApiActions.API_GET_CUSTOMER_STATE_EDIT,
                data: stateData
            });
            // Important customer state saved last since ui.hasCreatedFlexiApplication changes the view and destroys the form
            yield putAndHandleAPIError({
                type: lendingApiActions.API_SAVE_CUSTOMER_STATE
            });

            yield put({
                type: applyViewActions.APPLY_FLEXI_LOAN_SUBMIT_APPLICATION_SUCCESS
            });

            if (isTopUpApplication) {
                tracking.ampli.topupRequestCompleted({
                    application_id: application.application_id
                });
            } else {
                tracking.ampli.newLoanRequestCompleted({
                    application_id: application.application_id
                });
            }

            if (getCurrentLocation().pathname === '/apply-flexi-loan/') {
                yield put({
                    type: REQUEST_CHANGE_ROUTER,
                    location: { pathname: FLEXI_12_URL }
                });
            }
        }
    );
}
